import { FC } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { Modal, ModalBody } from "reactstrap";

import { AMENITY_CONSTANTS } from "@constants/amenities";
import { AmenityInfoModalProps } from "@models/components/amenity.model";
import { applyLocalization } from "@services/common.service";
import { formatPrice } from "@utils/common.utils";
import { sanitizeHtml } from "@utils/sanitize";

const AmenityInfoModal: FC<AmenityInfoModalProps> = ({
  amenity,
  currency = "",
  isOpen,
  maxQuantity,
  priceInfo,
  toggle,
}) => {
  const { t } = useTranslation();
  if (!amenity) return null;

  const title = applyLocalization(amenity.title);
  const description = applyLocalization(amenity.description);

  const { isVatInclusive, price, vat } = priceInfo;
  const formattedVat = Number.parseFloat(vat.toString());
  const vatText = isVatInclusive
    ? t("inclVat", { rate: formattedVat })
    : t("exclVat", { rate: formattedVat });

  const defaultMaxQuantity =
    amenity.allowMultipleQuantity > AMENITY_CONSTANTS.PER_AMENITY
      ? amenity.allowMultipleQuantity
      : (amenity.capacityManagement?.capacityCount ??
        AMENITY_CONSTANTS.DEFAULT_COUNT);

  const displayMaxQty = maxQuantity ?? defaultMaxQuantity;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="fg-amenity-info-modal"
      backdropClassName="fg-amenity-modal-backdrop"
      contentClassName="fg-modal-content"
    >
      <button
        type="button"
        className="fg-modal-close-btn"
        onClick={toggle}
        aria-label={t("close")}
      >
        <FaTimes />
      </button>
      <ModalBody className="fg-modal-body">
        <h3 className="fg-amenity-info-title">{title}</h3>
        <p className="fg-amenity-info-price">
          {formatPrice(price, currency)} {t("perUnit")}
        </p>
        <div
          className="fg-amenity-info-description"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(description),
          }}
        />
        <div className="fg-amenity-info-footer">
          {Number.parseFloat(vat.toString()) >
            AMENITY_CONSTANTS.DEFAULT_VAT_VALUE && (
            <>
              <span className="fg-vat-info">{vatText}</span>
              <span className="fg-dot">·</span>
            </>
          )}
          <span className="fg-max-qty-info">
            {t("maxQuantity")}: {displayMaxQty}
          </span>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AmenityInfoModal;
